// ─────────────────────────────────────────────
//  DoctorsNeededBox — the rail's only status box.
//
//  Replaces three chips (EMERGENCIES, UNCOVERED, BREAKS WAITING) and absorbs the
//  fourth (SHORT n AT 3 PM). Emergencies and breaks-waiting keep their home in
//  TimelineStatusBand, which reflows 3→2→1 columns but never disappears, so
//  nothing was lost there. UNCOVERED had no second home, which is why it
//  survives HERE as a line rather than as a chip of its own.
//
//  Presentational, like the rail around it: every value is a prop.
//
//  ── WHAT THE COLOUR MEANS ────────────────────────────────────────────────
//  Coming down in doctors is routine good news, and routine good news gets no
//  ink. Saturated colour on this rail is spent on the two things that can hurt
//  someone: a step that must NOT release a doctor (`holdExtra`), and a genuine
//  shortfall. Spending amber on the ordinary 14:00 step-down would teach people
//  to read past the 16:00 one that matters.
//
//  ── WHY `releasable` AND NOT `delta` ─────────────────────────────────────
//  `now.delta` is the surplus at this minute, and acting on it is the mistake
//  the whole box exists to prevent: two spare at 16:00 is not two who can go
//  home when 18:00 needs one back. Only `releasable` — measured against the
//  peak of the rest of the OR day — is safe to put next to the release button.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

const TONE = {
  calm: { ink: color.text.secondary, border: color.border.default, bg: color.surface.card },
  warn: { ink: "#7a4f00", border: color.accent.closing, bg: "#fef8ec" },
};

// Guarded matchMedia read — the same pattern GoHomeAlert and BreakStatusRow use.
// The ring is a SECONDARY signal (WCAG 2.3.3); the `data-imminent` flag and the
// "in N min" text carry the fact on their own when motion is suppressed.
function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// One crossing: the room count that caused it, when, and what it costs.
function StepRow({ step, reduceMotion }) {
  const hold = step.holdExtra > 0;
  const short = step.short > 0;
  const tone = hold || short ? TONE.warn : TONE.calm;
  const climbing = step.direction === "up";
  return (
    <div
      data-testid={`doctors-step-${step.min}`}
      data-hold={String(step.holdExtra || 0)}
      data-direction={step.direction}
      data-imminent={String(Boolean(step.imminent))}
      style={{
        ...s.step,
        color: tone.ink,
        background: tone.bg,
        borderColor: tone.border,
        // Ring, never opacity: the row must stay readable for every frame of
        // the blink. atcFlash would fade the numbers people are reading.
        ...(step.imminent && !reduceMotion
          ? { outline: `2px solid ${color.accent.closing}`, outlineOffset: 1, animation: "atcRing 1.1s steps(1) infinite" }
          : step.imminent
            ? { outline: `2px solid ${color.accent.closing}`, outlineOffset: 1 }
            : null),
      }}
    >
      <div style={s.stepMain}>
        {/* The direction is a WORD to a screen reader and a shape on screen,
            never colour alone. */}
        <span
          data-testid="doctors-direction"
          aria-label={climbing ? "more doctors needed" : "fewer doctors needed"}
          style={s.stepArrow}
        >
          {climbing ? "▲" : "▼"}
        </span>
        <span style={s.stepRooms}>{step.rooms}</span>
        <span style={s.stepUnit}>rms</span>
        <span style={s.stepTime}>{step.timeLabel}</span>
        <span style={s.stepDocs}>
          {step.required}
          <span style={s.stepUnit}> MD</span>
        </span>
        {hold && <span style={s.holdBadge}>+{step.holdExtra}</span>}
      </div>
      {hold && (
        <div style={s.stepNote}>
          ↳ back to {step.returnsTo} MD at {step.returnsAtLabel}
          {step.gapMin != null ? ` (${gapLabel(step.gapMin)})` : ""}
        </div>
      )}
      {short && <div style={s.stepNote}>▲ {step.short} short here</div>}
      {step.imminent && step.minutesAway != null && (
        <div style={s.stepNote}>in {plural(step.minutesAway, "min", "min")}</div>
      )}
    </div>
  );
}

function gapLabel(min) {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h${m}` : `${h}h`;
}

export default function DoctorsNeededBox({
  now = null,
  steps,
  releasable = null,
  uncovered = null,
  uncoveredUnavailable = false,
  uncoveredHomeFacilityName = "",
  planned = false,
  maxSteps = 5,
  canEdit = false,
  editLockReason = "",
  onExtraDoctor,
  // Open/closed is the CALLER's state, and so is the menu itself: releasing a
  // doctor needs the roster, the assignments and the staff pool, none of which
  // belong in a presentational box. It arrives as a node and is rendered under
  // the button. See DoctorReleaseMenu.
  releaseMenu = null,
  releaseOpen = false,
}) {
  const list = Array.isArray(steps) ? steps : [];
  const shown = list.slice(0, maxSteps);
  const hidden = list.length - shown.length;
  const reduceMotion = prefersReducedMotion();
  const shortNow = now && now.short > 0;

  return (
    <section style={s.card} data-testid="doctors-needed" aria-labelledby="doctors-needed-heading">
      <h2 id="doctors-needed-heading" style={s.cardLabel}>
        DOCTORS NEEDED{planned ? " · PLANNED" : ""}
      </h2>

      {/* ── THE LIVE READING ─────────────────────────────────────────────
          Withheld entirely on a board that is not today. `claimClockFor` hands
          the page a null clock on any other date, and a "0 MD needed" printed
          from a null clock is indistinguishable from a genuinely empty board. */}
      {now ? (
        <div
          style={{ ...s.now, color: shortNow ? TONE.warn.ink : color.text.primary }}
          data-testid="doctors-now"
          data-tone={shortNow ? "warn" : "calm"}
        >
          <div style={s.bigNumber}>
            {now.required}
            <span style={s.bigNumberUnit}> MD needed</span>
          </div>
          <div style={s.subLine}>
            {plural(now.rooms, "room", "rooms")} running
            {now.soloRooms > 0 ? ` · ${now.soloRooms} solo` : ""}
          </div>
          <div style={s.subLine}>
            {shortNow
              ? `▲ ${now.short} short — ${now.available} on call`
              : `${now.available} on call · ${
                  releasable > 0 ? `${releasable} can go` : "none to release"
                }`}
          </div>
        </div>
      ) : (
        <div style={s.plannedNote} data-testid="doctors-planned">
          Projection only — this is not today's board, so there is no live count.
        </div>
      )}

      {/* ── UNCOVERED ────────────────────────────────────────────────────
          Kept because removing the chip left it with no other surface on this
          page. Alert-derived, so it refuses rather than reporting a zero when
          the alert bus is following a different campus. */}
      <div
        style={{
          ...s.uncovered,
          ...(uncoveredUnavailable
            ? s.uncoveredUnavailable
            : uncovered > 0
              ? { color: TONE.warn.ink, background: TONE.warn.bg, borderColor: TONE.warn.border }
              : null),
        }}
        data-testid="doctors-uncovered"
        data-tone={uncoveredUnavailable ? "unavailable" : uncovered > 0 ? "warn" : "calm"}
      >
        {uncoveredUnavailable ? (
          <span style={s.uncoveredMuted}>
            UNCOVERED — not available
            {uncoveredHomeFacilityName ? ` · alerts follow ${uncoveredHomeFacilityName}` : ""}
          </span>
        ) : (
          <>
            <span style={s.uncoveredCount}>{uncovered || 0}</span>
            <span style={s.uncoveredLabel}>UNCOVERED</span>
          </>
        )}
      </div>

      <div style={s.divider} />

      {/* ── THE CROSSINGS ───────────────────────────────────────────────── */}
      <div style={s.steps} data-testid="doctors-steps">
        {shown.length === 0 ? (
          <div style={s.emptyNote}>No change in doctors for the rest of the day.</div>
        ) : (
          shown.map((step) => (
            <StepRow key={step.min} step={step} reduceMotion={reduceMotion} />
          ))
        )}
        {hidden > 0 && <div style={s.moreNote}>+{hidden} more later</div>}
      </div>

      {/* ── RELEASE ──────────────────────────────────────────────────────
          Never disabled when it is shown. A manager may want to transfer or
          unassign a doctor for reasons the ratio knows nothing about, and a
          dead button gives them no reason why. The menu refuses, in words, with
          the consequence spelled out. */}
      {canEdit && !planned && (
        <>
          <button
            type="button"
            style={s.extraBtn}
            onClick={onExtraDoctor}
            // ⚠️ NO aria-haspopup="menu". What opens is a labelled GROUP of
            // rows, each carrying DepartureFlow's own button — not a menu, and
            // announcing one promises arrow keys that cannot work through a
            // foreign trigger. `aria-expanded` alone is the honest contract for
            // a disclosure. See DoctorReleaseMenu's header.
            aria-expanded={releaseOpen}
            data-testid="doctors-extra-btn"
          >
            Extra doctor {releaseOpen ? "▴" : "▾"}
          </button>
          {releaseMenu}
        </>
      )}
      {/* ⚠️ ONE REFUSAL PER ABSENCE. The lock note explains why a control that
          WOULD be here is not — so it must stay silent on a planned board,
          where the control was never on offer and "Projection only — this is
          not today's board" above has already said so. Showing both put two
          reasons under one missing button, and the campus one is actively
          misleading there: resolving the device would not make a past board
          editable. Caught on a live 2026-09-06 board; the unit suite cannot
          see it because `planned` and `editLockReason` are set independently
          in tests and never contradict each other. */}
      {!canEdit && !planned && editLockReason && (
        <div style={s.lockNote} data-testid="doctors-edit-lock">
          {editLockReason}
        </div>
      )}
    </section>
  );
}

const s = {
  card: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    padding: "9px 10px 11px",
  },
  cardLabel: {
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: ".7px",
    color: color.text.muted,
    margin: 0,
  },

  now: { marginTop: 2 },
  bigNumber: { fontSize: 21, fontWeight: font.weight.bold, lineHeight: 1.1 },
  bigNumberUnit: { fontSize: 10.5, fontWeight: font.weight.regular, color: color.text.secondary },
  subLine: { fontSize: 10.5, color: color.text.secondary, marginTop: 2, lineHeight: 1.3 },
  plannedNote: { fontSize: 10, lineHeight: 1.4, color: color.text.secondary, marginTop: 4 },

  uncovered: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
    minHeight: 26,
    padding: "3px 7px",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    color: color.text.secondary,
  },
  uncoveredUnavailable: {
    background: "repeating-linear-gradient(45deg, #cfd8e6 0 4px, #eef2f8 4px 8px)",
    borderColor: color.text.muted,
    borderStyle: "dashed",
  },
  uncoveredCount: { fontSize: 14, fontWeight: font.weight.bold, minWidth: 12, textAlign: "right" },
  uncoveredLabel: { fontSize: 9, fontWeight: font.weight.bold, letterSpacing: ".4px" },
  uncoveredMuted: { fontSize: 9, fontWeight: font.weight.bold, letterSpacing: ".4px", color: color.text.muted, lineHeight: 1.3 },

  divider: { height: 1, background: color.border.default, margin: "9px 0" },

  steps: { display: "flex", flexDirection: "column", gap: 4 },
  step: {
    padding: "4px 6px",
    border: "1px solid",
    borderRadius: radius.sm,
    // ⚠️ A 179px RAIL. Every one of these columns has to fit beside the others,
    // so the numbers are tabular and the units are the only thing that shrinks.
    fontVariantNumeric: "tabular-nums",
  },
  stepMain: { display: "flex", alignItems: "baseline", gap: 3 },
  stepArrow: { fontSize: 8, opacity: 0.75 },
  stepRooms: { fontSize: 12, fontWeight: font.weight.bold, minWidth: 15, textAlign: "right" },
  stepUnit: { fontSize: 8.5, opacity: 0.75 },
  stepTime: { fontSize: 11, fontWeight: font.weight.bold, marginLeft: "auto" },
  stepDocs: { fontSize: 12, fontWeight: font.weight.bold, minWidth: 30, textAlign: "right" },
  holdBadge: {
    fontSize: 9,
    fontWeight: font.weight.bold,
    background: color.accent.closing,
    color: "#fff",
    borderRadius: radius.full,
    padding: "0 4px",
  },
  stepNote: { fontSize: 9, lineHeight: 1.35, marginTop: 1, opacity: 0.9 },
  emptyNote: { fontSize: 10, color: color.text.secondary, lineHeight: 1.35 },
  moreNote: { fontSize: 9, color: color.text.muted, marginTop: 1 },

  extraBtn: {
    marginTop: 9,
    width: "100%",
    // 44px: this rail also renders stacked on a phone, and this is its only
    // interactive control.
    minHeight: 44,
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: ".3px",
    color: color.text.primary,
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  lockNote: { fontSize: 9, color: color.text.muted, marginTop: 8, lineHeight: 1.35 },
};
